import axios from "axios";
import { REQUEST, SUCCESS, FAILURE } from "../action-type.util";
export const ACTION_TYPES = {
  GET_NOTIFICATIONS: "/GET_NOTIFICATIONS",
  LOAD_MORE_NOTIFICATIONS: "/LOAD_MORE_NOTIFICATIONS",
  PREPEND_NOTIFICATION: "/PREPEND_NOTIFICATION",
  NOTIFICTAION_MARKED_READ_SINGLE: "/NOTIFICTAION_MARKED_READ_SINGLE",
  NOTIFICATION_MARKED_AS_READ: "/NOTIFICATION_MARKED_AS_READ",
  RESET_NOTIFICATIONS: "/RESET_NOTIFICATIONS",
  // Day 21.13 — optimistic UI: flip isRead in Redux BEFORE the API
  // call so the UI updates instantly. SUCCESS becomes a no-op
  // (optimistic update already applied); FAILURE reverts.
  OPTIMISTIC_MARK_READ_SINGLE: "/OPTIMISTIC_MARK_READ_SINGLE",
  OPTIMISTIC_REVERT_READ_SINGLE: "/OPTIMISTIC_REVERT_READ_SINGLE",
  OPTIMISTIC_MARK_ALL_READ: "/OPTIMISTIC_MARK_ALL_READ",
  OPTIMISTIC_REVERT_ALL_READ: "/OPTIMISTIC_REVERT_ALL_READ",
};

const initialState = {
  loading: false,
  loadingMore: false,
  allNotifications: [],
  page: 1,
  totalPages: 1,
  totalResults: 0,
  unreadCount: 0,
  notificationForEdit: undefined,
  error: undefined,
};

export const NotificationState = initialState;

const NOTIFICATION_LIMIT = 15;

// Reducer
// eslint-disable-next-line
export default (state = initialState, action) => {
  switch (action.type) {
    case REQUEST(ACTION_TYPES.GET_NOTIFICATIONS):
      return {
        ...state,
        error: undefined,
        loading: true,
      };
    case REQUEST(ACTION_TYPES.LOAD_MORE_NOTIFICATIONS):
      return {
        ...state,
        error: undefined,
        loadingMore: true,
      };
    case REQUEST(ACTION_TYPES.NOTIFICTAION_MARKED_READ_SINGLE):
    case REQUEST(ACTION_TYPES.NOTIFICATION_MARKED_AS_READ):
      return {
        ...state,
        error: undefined,
      };
    case FAILURE(ACTION_TYPES.GET_NOTIFICATIONS):
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case FAILURE(ACTION_TYPES.LOAD_MORE_NOTIFICATIONS):
      return {
        ...state,
        error: action.payload,
        loadingMore: false,
      };
    case FAILURE(ACTION_TYPES.NOTIFICTAION_MARKED_READ_SINGLE):
    case FAILURE(ACTION_TYPES.NOTIFICATION_MARKED_AS_READ):
      return {
        ...state,
        error: action.payload,
      };
    case SUCCESS(ACTION_TYPES.GET_NOTIFICATIONS): {
      const data = action.payload.data;
      return {
        ...state,
        loading: false,
        allNotifications: data.results,
        page: data.page,
        totalPages: data.totalPages,
        totalResults: data.totalResults,
        unreadCount: data.unreadCount ?? 0,
      };
    }
    case SUCCESS(ACTION_TYPES.LOAD_MORE_NOTIFICATIONS): {
      const data = action.payload.data;
      return {
        ...state,
        loadingMore: false,
        allNotifications: [...state.allNotifications, ...data.results],
        page: data.page,
        totalPages: data.totalPages,
        totalResults: data.totalResults,
        unreadCount: data.unreadCount ?? state.unreadCount,
      };
    }
    case ACTION_TYPES.PREPEND_NOTIFICATION:
      return {
        ...state,
        allNotifications: [action.payload, ...state.allNotifications],
        totalResults: state.totalResults + 1,
        unreadCount: state.unreadCount + 1,
      };
    case SUCCESS(ACTION_TYPES.NOTIFICTAION_MARKED_READ_SINGLE):
    case SUCCESS(ACTION_TYPES.NOTIFICATION_MARKED_AS_READ):
      // Optimistic update was already applied via the OPTIMISTIC_* action
      // before the API call. Nothing left to do on success.
      return state;

    // ─── Day 21.13 — Optimistic UI ───────────────────────────────────────
    case ACTION_TYPES.OPTIMISTIC_MARK_READ_SINGLE: {
      // Flip isRead + decrement unreadCount IF the notification was
      // previously unread (idempotent — double-clicks don't double-decrement).
      const targetId = action.payload?.id;
      let didFlip = false;
      const next = state.allNotifications.map((n) => {
        const match = n.id === targetId || n._id === targetId;
        if (match && !n.isRead) {
          didFlip = true;
          return { ...n, isRead: true };
        }
        return n;
      });
      return {
        ...state,
        allNotifications: next,
        unreadCount: didFlip ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }
    case ACTION_TYPES.OPTIMISTIC_REVERT_READ_SINGLE: {
      // API failed — undo the optimistic flip.
      const targetId = action.payload?.id;
      let didRevert = false;
      const next = state.allNotifications.map((n) => {
        const match = n.id === targetId || n._id === targetId;
        if (match && n.isRead) {
          didRevert = true;
          return { ...n, isRead: false };
        }
        return n;
      });
      return {
        ...state,
        allNotifications: next,
        unreadCount: didRevert ? state.unreadCount + 1 : state.unreadCount,
      };
    }
    case ACTION_TYPES.OPTIMISTIC_MARK_ALL_READ: {
      // Snapshot was attached to action.payload.previouslyUnreadIds so the
      // revert path can restore only the IDs we actually flipped (not ones
      // already-read before).
      return {
        ...state,
        allNotifications: state.allNotifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      };
    }
    case ACTION_TYPES.OPTIMISTIC_REVERT_ALL_READ: {
      const idsToRevert = new Set(action.payload?.previouslyUnreadIds || []);
      return {
        ...state,
        allNotifications: state.allNotifications.map((n) => {
          const id = n.id || n._id;
          return idsToRevert.has(id) ? { ...n, isRead: false } : n;
        }),
        unreadCount: state.unreadCount + idsToRevert.size,
      };
    }
    case ACTION_TYPES.RESET_NOTIFICATIONS:
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

// Actions

export const getAllNotifications = () => async (dispatch) => {
  return await dispatch({
    type: ACTION_TYPES.GET_NOTIFICATIONS,
    payload: axios.get(`/notifications?page=1&limit=${NOTIFICATION_LIMIT}`),
  });
};

export const loadMoreNotifications = (page) => async (dispatch) => {
  return await dispatch({
    type: ACTION_TYPES.LOAD_MORE_NOTIFICATIONS,
    payload: axios.get(`/notifications?page=${page}&limit=${NOTIFICATION_LIMIT}`),
  });
};

// Day 21.13 — Optimistic mark-as-read:
// 1) Flip isRead in Redux IMMEDIATELY (the UI re-renders without waiting).
// 2) Fire the API call.
// 3) If the API fails, dispatch the revert action so the UI snaps back.
export const notificationReadSingle = (id, data) => async (dispatch) => {
  dispatch({ type: ACTION_TYPES.OPTIMISTIC_MARK_READ_SINGLE, payload: { id } });
  try {
    return await dispatch({
      type: ACTION_TYPES.NOTIFICTAION_MARKED_READ_SINGLE,
      payload: axios.patch(`/notifications/${id}`, data),
    });
  } catch (err) {
    dispatch({ type: ACTION_TYPES.OPTIMISTIC_REVERT_READ_SINGLE, payload: { id } });
    throw err;
  }
};

export const notificationMarkedAsRead = () => async (dispatch, getState) => {
  // Snapshot the currently-unread ids so we can revert only those if the
  // API fails (not the ones that were already read before).
  const previouslyUnreadIds = getState()
    .notifications.allNotifications.filter((n) => !n.isRead)
    .map((n) => n.id || n._id);

  dispatch({ type: ACTION_TYPES.OPTIMISTIC_MARK_ALL_READ });
  try {
    return await dispatch({
      type: ACTION_TYPES.NOTIFICATION_MARKED_AS_READ,
      payload: axios.get(`/notifications/markedAsRead`),
    });
  } catch (err) {
    dispatch({
      type: ACTION_TYPES.OPTIMISTIC_REVERT_ALL_READ,
      payload: { previouslyUnreadIds },
    });
    throw err;
  }
};
